import React, { useEffect, useRef, useState } from "react";
import CountryList from "country-list-with-dial-code-and-flag";
import Select from "react-select";
import DateRangePicker from "./DateRangePicker";
import DualThumbSlider from "./DualThumbSlider";
import { Tooltip } from "flowbite-react";
import { Icon } from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import UpgradeModal from "./UpgradeModal";
import { getSubscriptionPlanAmount, hasMenuAccess, isSubscriptionPlanGreaterThan, isUserOwnerOfWorkspace } from "../lib/utils";

const LevelFilters = ({
  filters,
  onFilterChange,
  filterInfo = {},
  isVisible,
  onClose,
}) => {
  const [isExclusionModalOpen, setIsExclusionModalOpen] = useState(false);
  const [exclusionOptions, setExclusionOptions] = useState({
    industries: [],
    employeeSizes: [],
    companyTypes: [],
    sectors: [],
  });
  const [selectedExclusionOptions, setSelectedExclusionOptions] = useState({
    industries: [],
    employeeSizes: [],
    companyTypes: [],
    sectors: [],
  });
  const [isLoadingExclusions, setIsLoadingExclusions] = useState(false);
  const [isSavingExclusions, setIsSavingExclusions] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  // Check if current user is the workspace owner
  const isOwner = isUserOwnerOfWorkspace();

  // Target Countries state
  const [isTargetCountriesModalOpen, setIsTargetCountriesModalOpen] = useState(false);
  const [selectedTargetCountries, setSelectedTargetCountries] = useState([]);
  const [isLoadingTargetCountries, setIsLoadingTargetCountries] = useState(false);
  const [isSavingTargetCountries, setIsSavingTargetCountries] = useState(false);

  // All countries list from country-list package
  const allCountries = CountryList.getAll({ withSecondary: false }).map((c) => ({
    value: c.code,
    label: `${c.flag} ${c.name}`,
  }));

  // Hardcoded category display names
  const categoryDisplayNames = {
    industries: "INDUSTRIES TO EXCLUDE",
    employeeSizes: "EMPLOYEE SIZE TO EXCLUDE",
    companyTypes: "COMPANY TYPE TO EXCLUDE",
    sectors: "SECTORS TO EXCLUDE",
  };

  // Fetch exclusion filters when modal opens
  useEffect(() => {
    if (isExclusionModalOpen) {
      fetchExclusionFilters();
    }
  }, [isExclusionModalOpen]);

  // Fetch target countries when modal opens
  useEffect(() => {
    if (isTargetCountriesModalOpen) {
      fetchTargetCountries();
    }
  }, [isTargetCountriesModalOpen]);

  const fetchExclusionFilters = async () => {
    setIsLoadingExclusions(true);
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/visonleads/leads/exclusion_filter`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        params: {
          // workspace_id:
          //   JSON.parse(localStorage.getItem("selected_workspace"))
          //     ?.workspace_id || null,
          // website_id: JSON.parse(localStorage.getItem("selected_website"))?.website_id || null,
        },
      });

      if (!response.data.error) {
        const { company_type,employee_type,industry,sectors,exclusions } =
          response?.data?.data
          // console.log(response?.data)
        setExclusionOptions({
          industries: industry?.map(industry => industry?.name) || [],
          employeeSizes: employee_type || [],
          companyTypes: company_type || [],
          sectors: sectors || [],
        });
        setSelectedExclusionOptions({
          industries: exclusions?.industries || [],
          employeeSizes: exclusions.employeesizes || [],
          companyTypes: exclusions.companytypes || [],
          sectors: exclusions.sectors || [],
        });
      }
    } catch (error) {
      console.error("Error fetching exclusion filters:", error);
    } finally {
      setIsLoadingExclusions(false);
    }
  };

  const toggleExclusionOption = (categoryKey, value) => {
    setSelectedExclusionOptions((prev) => {
      const currentSelections = prev[categoryKey] || [];
      const updatedCategory = currentSelections.includes(value)
        ? currentSelections.filter((v) => v !== value)
        : [...currentSelections, value];

      return {
        ...prev,
        [categoryKey]: updatedCategory,
      };
    });
  };

  const clearExclusionCategory = (categoryKey) => {
    setSelectedExclusionOptions((prev) => ({
      ...prev,
      [categoryKey]: [],
    }));
  };

  const clearExclusions = () => {
    setSelectedExclusionOptions({
      industries: [],
      employeeSizes: [],
      companyTypes: [],
      sectors: [],
    });
  };

  const handleSaveExclusions = async () => {
    setIsSavingExclusions(true);
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/visonleads/leads/exclusion_filter`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: {
            // workspace_id:
            //   JSON.parse(localStorage.getItem("selected_workspace"))
            //     ?.workspace_id || null,
          // website_id: JSON.parse(localStorage.getItem("selected_website"))?.website_id || null,
          ...selectedExclusionOptions,
        },
      });

      if (!response.data.error) {
        const message =
          response?.data?.message || "Exclusion filters saved successfully";
        toast(message, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
          type: "success",
        });
        setIsExclusionModalOpen(false);
        // Optionally refresh the leads data here if needed
      }
    } catch (error) {
      console.error("Error saving exclusion filters:", error);
    } finally {
      setIsSavingExclusions(false);
    }
  };

  const handleCancelExclusions = () => {
    // Reset to original values by fetching again
    fetchExclusionFilters();
    setIsExclusionModalOpen(false);
  };

  // Fetch already selected target countries from API
  const fetchTargetCountries = async () => {
    setIsLoadingTargetCountries(true);
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/user/lead/country_filter`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
      });
      if (!response.data.error) {
        const countries = response?.data?.data || [];
        // Normalise array of ISO codes to { value, label } for react-select
        const normalised = countries.map((code) => {
          const found = allCountries.find((ac) => ac.value === code);
          return found || { value: code, label: code };
        });
        setSelectedTargetCountries(normalised);
      }
    } catch (error) {
      console.error("Error fetching target countries:", error);
    } finally {
      setIsLoadingTargetCountries(false);
    }
  };

  // Save target countries via API
  const handleSaveTargetCountries = async () => {
    setIsSavingTargetCountries(true);
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/user/lead/country_filter`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: {
          country_data: selectedTargetCountries.map((c) => c.value),
        },
      });
      if (!response.data.error) {
        const message = response?.data?.message || "Target countries saved successfully";
        toast(message, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
          type: "success",
        });
        setIsTargetCountriesModalOpen(false);
      }
    } catch (error) {
      console.error("Error saving target countries:", error);
    } finally {
      setIsSavingTargetCountries(false);
    }
  };

  // Generate dynamic options from filterInfo
  const generateOptions = (filterKey, data = []) => {
    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }

    // Handle visitor location data structure (city, country)
    if (filterKey === "visitorlocation") {
      const locations = new Set();
      data.forEach((item) => {
        // const location = `city:${item?.city || ""}-state:${
        //   item?.state || ""
        // }-country:${item?.country || ""}`;
        const location = [
          item?.city ? `city:${item.city}` : "",
          item?.state ? `state:${item.state}` : "",
          item?.country ? `country:${item.country}` : "",
        ]
          .filter(Boolean)
          .join("-");
        locations.add(location);
      });

      return Array.from(locations).map((location) => ({
        value: location,
        label: location
          ?.replace(/city:/g, "")
          .replace(/state:/g, "")
          .replace(/country:/g, ""),
      }));
    }

    // Handle company location data structure (state, country)
    if (filterKey === "companylocation") {
      const locations = new Set();
      data.forEach((item) => {
        if (item.state && item.country) {
          const location = `${item.state}, ${item.country}`;
          locations.add(location);
        }
      });
      return Array.from(locations)
        .sort()
        .map((location) => ({
          value: location,
          label: location,
        }));
    }

    // Handle other arrays of strings - remove duplicates and sort
    const uniqueItems = [...new Set(data)].sort();
    return uniqueItems.map((item) => ({
      value: item,
      label: item,
    }));
  };
  // Custom styles for react-select
  const getSelectStyles = (isDisabled) => ({
    control: (provided, state) => ({
      ...provided,
      backgroundColor: isDisabled ? "#f5f5f5" : "#ffffff",
      borderColor: isDisabled
        ? "#d1d5db"
        : state.isFocused
        ? "#5e78f4"
        : "#d1d5db",
      borderRadius: "12px",
      minHeight: "42px",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(94, 120, 244, 0.1)" : "none",
      cursor: isDisabled ? "not-allowed" : "default",
      opacity: isDisabled ? 0.5 : 1,
      "&:hover": {
        borderColor: isDisabled ? "#d1d5db" : "#5e78f4",
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "2px 8px",
    }),
    input: (provided) => ({
      ...provided,
      margin: "0px",
      padding: "0px",
      outline: "none",
      outlineOffset: 0,
      boxShadow: "none",
      border: "none",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#e8f2ff",
      borderRadius: "8px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#2563eb",
      fontSize: "14px",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#6b7280",
      "&:hover": {
        backgroundColor: "#dc2626",
        color: "white",
      },
    }),
    placeholder: (provided, state) => ({
      ...provided,
      color: isDisabled ? "#9ca3af" : "#6b7280",
      fontSize: "14px",
      display: state.isFocused ? "none" : "block",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#5e78f4"
        : state.isFocused
        ? "#f3f4f6"
        : "white",
      color: state.isSelected ? "white" : "#374151",
      fontSize: "14px",
      cursor: "pointer",
    }),
  });

  // Handle filter changes for react-select
  const handleSelectChange = (filterName, selectedOptions) => {
    const values = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    onFilterChange({
      ...filters,
      [filterName]: values,
    });
  };

  // Get current values for react-select
  const getSelectValue = (filterName) => {
    const currentValues = filters[filterName] || [];
    return currentValues.map((value) => ({
      value,
      label: value
        ?.replace(/city:/g, "")
        .replace(/state:/g, "")
        .replace(/country:/g, ""),
    }));
  };

  // Check if a filter should be disabled (no data available)
  const isFilterDisabled = (filterKey) => {
    const data = filterInfo[filterKey];
    return !data || !Array.isArray(data) || data.length === 0;
  };

  // Check if a slider should be disabled (no range data available)
  const isSliderDisabled = (filterKey) => {
    const data = filterInfo[filterKey];
    return (
      !data ||
      typeof data !== "object" ||
      data.start === undefined ||
      data.end === undefined
    );
  };

  // Get slider range from filterInfo
  const getSliderRange = (filterKey, defaultMin = 0, defaultMax = 100) => {
    const data = filterInfo[filterKey];
    if (
      data &&
      typeof data === "object" &&
      data.start !== undefined &&
      data.end !== undefined
    ) {
      return { min: data.start, max: data.end };
    }
    return { min: defaultMin, max: defaultMax };
  };

  // Handle identity level change
  const handleIdentityLevelChange = (filterName, value) => {
    onFilterChange({
      ...filters,
      [filterName]: [value],
    });
  };
  // Handle range/slider changes
  const handleRangeChange = (filterName, value) => {
    onFilterChange({
      ...filters,
      [filterName]: value,
    });
  };

  const handleClearFilters = () => {
    onFilterChange({
      identityLevel: filters.identityLevel || [],
      lastVisitDate: filters.lastVisitDate || null,
      visitorLocation: [],
      industry: [],
      sector: [],
      // companyHQ: [],
      // designationTitle: [],
      // tags: [],
      // icpScore: [],
      // numberOfVisits: [],
      exactVisitor: [],
      hasPhoneNumber: [],
      revenue: [],
      // employees: [],
      _clearSliders: true, // Flag to indicate sliders should be treated as "unset"
    });
  };

  return (
    <div
      className={`bg-white border-r border-gray-200 h-screen overflow-y-auto transition-all duration-300 min-w-[204px] max-w-[204px] ${
        isVisible ? "w-80" : "w-0"
      }`}
    >
      {isVisible && (
        <div className="p-3">
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
          <h3 className="text-sm font-medium text-[#2a2a2a]">Filters</h3>

            {/* clear filters button */}
            <button
              onClick={handleClearFilters}
              className="text-sm font-medium text-blue-500 hover:text-blue-700 "
            >
              Clear All
            </button>
          </div>

          <div className="mb-4">
            {hasMenuAccess("exclusion_filter") ? (
              isOwner ? (
                <button
                  type="button"
                  onClick={() => setIsExclusionModalOpen(true)}
                  className="font-normal text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <span>Exclusion filters</span>
                  <span>{">"}</span>
                </button>
              ) : (
                <Tooltip
                  content="Only workspace owner can change this setting"
                  trigger="hover"
                  style="light"
                  placement="bottom"
                >
                  <button
                    type="button"
                    className="font-normal text-gray-400 flex items-center gap-1 cursor-not-allowed"
                  >
                    <span>Exclusion filters</span>
                    <span>{">"}</span>
                  </button>
                </Tooltip>
              )
            ) : (
              <Tooltip
                content="Please upgrade your plan to access this feature."
                trigger="hover"
                style="light"
                placement="bottom"
              >
                <button
                  type="button"
                  onClick={() => setIsUpgradeModalOpen(true)}
                  className="font-normal text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <span>Exclusion filters</span>
                  <span>{">"}</span>
                </button>
              </Tooltip>
            )}
          </div>

          {/* Target Countries */}
          <div className="mb-8">
            {isSubscriptionPlanGreaterThan(229) ? (
              isOwner ? (
                <button
                  type="button"
                  onClick={() => setIsTargetCountriesModalOpen(true)}
                  className="font-normal text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <span>Target Countries</span>
                  <span>{">"}</span>
                </button>
              ) : (
                <Tooltip
                  content="Only workspace owner can change this setting"
                  trigger="hover"
                  style="light"
                  placement="bottom"
                >
                  <button
                    type="button"
                    className="font-normal text-gray-400 flex items-center gap-1 cursor-not-allowed"
                  >
                    <span>Target Countries</span>
                    <span>{">"}</span>
                  </button>
                </Tooltip>
              )
            ) : (
              <Tooltip
                content="Please upgrade your plan to access this feature."
                trigger="hover"
                style="light"
                placement="bottom"
              >
                <button
                  type="button"
                  onClick={() => {
                    setIsUpgradeModalOpen(229)
                  }}
                  className="font-normal text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <span>Target Countries</span>
                  <span>{">"}</span>
                </button>
              </Tooltip>
            )}
          </div>

          {/* 1. Identity Level - radio buttons - static */}
          <div className="mb-8">
            <label className="block text-base font-normal text-[#2a2a2a] mb-3">
              Identity Level
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="identityLevel"
                  value="person"
                  checked={filters.identityLevel?.includes("person")}
                  onChange={(e) =>
                    handleIdentityLevelChange("identityLevel", e.target.value)
                  }
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600">
                  Person Level Identity
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="identityLevel"
                  value="company"
                  checked={filters.identityLevel?.includes("company")}
                  onChange={(e) =>
                    handleIdentityLevelChange("identityLevel", e.target.value)
                  }
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600">
                  Company Level Identity
                </span>
              </label>
            </div>
          </div>

          {/* 2. Last Visit Date - daterangepicker - static */}
          <div className="mb-8">
            <DateRangePicker
              label="Last Visit Date"
              value={filters?.lastVisitDate}
              onChange={(val) => handleRangeChange("lastVisitDate", val)}
            />
          </div>

          {/* 3. Visitor Location - dropdown - dynamic */}
          <div
            className={`mb-8 ${
              isFilterDisabled("visitorlocation") ? "opacity-50" : ""
            }`}
          >
            <label className="block text-base font-normal text-[#2a2a2a] mb-3">
              Visitor Location
              {isFilterDisabled("visitorlocation") && (
                <span className="text-xs text-gray-400 ml-2">
                  (No data available)
                </span>
              )}
            </label>
            <Select
              hideSelectedOptions={false}
              isClearable={false}
              isMulti
              isDisabled={isFilterDisabled("visitorlocation")}
              isSearchable
              placeholder="Search and select locations..."
              options={generateOptions(
                "visitorlocation",
                filterInfo.visitorlocation
              )}
              value={getSelectValue("visitorLocation")}
              onChange={(selectedOptions) =>
                handleSelectChange("visitorLocation", selectedOptions)
              }
              styles={getSelectStyles(isFilterDisabled("visitorlocation"))}
              noOptionsMessage={() => "No visitor locations available"}
              className="filter-select"
              classNamePrefix="filter-select"
            />
          </div>

          {/* 9. Industry - dropdown - dynamic */}
          <div
            className={`mb-8 ${
              isFilterDisabled("industries") ? "opacity-50" : ""
            }`}
          >
            <label className="block text-base font-normal text-[#2a2a2a] mb-3">
              Industry
              {isFilterDisabled("industries") && (
                <span className="text-xs text-gray-400 ml-2">
                  (No data available)
                </span>
              )}
            </label>
            <Select
              hideSelectedOptions={false}
              isClearable={false}
              isMulti
              isDisabled={isFilterDisabled("industries")}
              isSearchable
              placeholder="Search and select industries..."
              options={generateOptions("industries", filterInfo.industries)}
              value={getSelectValue("industry")}
              onChange={(selectedOptions) =>
                handleSelectChange("industry", selectedOptions)
              }
              styles={getSelectStyles(isFilterDisabled("industries"))}
              noOptionsMessage={() => "No industries available"}
              className="filter-select"
              classNamePrefix="filter-select"
            />
          </div>

          {/* 10. Sector - dropdown - dynamic */}
          <div
            className={`mb-8 ${
              isFilterDisabled("sectors") ? "opacity-50" : ""
            }`}
          >
            <label className="block text-base font-normal text-[#2a2a2a] mb-3">
              Sector
              {isFilterDisabled("sectors") && (
                <span className="text-xs text-gray-400 ml-2">
                  (No data available)
                </span>
              )}
            </label>
            <Select
              hideSelectedOptions={false}
              isClearable={false}
              isMulti
              isDisabled={isFilterDisabled("sectors")}
              isSearchable
              placeholder="Search and select sectors..."
              options={generateOptions("sectors", filterInfo.sectors)}
              value={getSelectValue("sector")}
              onChange={(selectedOptions) =>
                handleSelectChange("sector", selectedOptions)
              }
              styles={getSelectStyles(isFilterDisabled("sectors"))}
              noOptionsMessage={() => "No sectors available"}
              className="filter-select"
              classNamePrefix="filter-select"
            />
          </div>

          {/* Company HQ Location - dropdown - dynamic - COMMENTED OUT */}
          {/* <div
            className={`mb-8 ${
              isFilterDisabled("companylocation") ? "opacity-50" : ""
            }`}
          >
            <label className="block text-base font-normal text-[#2a2a2a] mb-3">
              Company HQ Location
              {isFilterDisabled("companylocation") && (
                <span className="text-xs text-gray-400 ml-2">
                  (No data available)
                </span>
              )}
            </label>
            <Select
              hideSelectedOptions={false}
              isClearable={false}
              isMulti
              isDisabled={isFilterDisabled("companylocation")}
              isSearchable
              placeholder="Search and select HQ locations..."
              options={generateOptions(
                "companylocation",
                filterInfo.companylocation
              )}
              value={getSelectValue("companyHQ")}
              onChange={(selectedOptions) =>
                handleSelectChange("companyHQ", selectedOptions)
              }
              styles={getSelectStyles(isFilterDisabled("companylocation"))}
              noOptionsMessage={() => "No HQ locations available"}
            />
          </div> */}

          {/* Designation or Title - dropdown - dynamic - COMMENTED OUT */}
          {/* <div
            className={`mb-8 ${
              isFilterDisabled("jobtitles") ? "opacity-50" : ""
            }`}
          >
            <label className="block text-base font-normal text-[#2a2a2a] mb-3">
              Designation or Title
              {isFilterDisabled("jobtitles") && (
                <span className="text-xs text-gray-400 ml-2">
                  (No data available)
                </span>
              )}
            </label>
            <Select
              hideSelectedOptions={false}
              isClearable={false}
              isMulti
              isDisabled={isFilterDisabled("jobtitles")}
              isSearchable
              placeholder="Search and select designations..."
              options={generateOptions("jobtitles", filterInfo.jobtitles)}
              value={getSelectValue("designationTitle")}
              onChange={(selectedOptions) =>
                handleSelectChange("designationTitle", selectedOptions)
              }
              styles={getSelectStyles(isFilterDisabled("jobtitles"))}
              noOptionsMessage={() => "No designations available"}
            />
          </div> */}

          {/* Tags - dropdown - dynamic - COMMENTED OUT */}
          {/* <div
            className={`mb-8 ${isFilterDisabled("tags") ? "opacity-50" : ""}`}
          >
            <label className="block text-base font-normal text-[#2a2a2a] mb-3">
              Tags
              {isFilterDisabled("tags") && (
                <span className="text-xs text-gray-400 ml-2">
                  (No data available)
                </span>
              )}
            </label>
            <Select
              hideSelectedOptions={false}
              isClearable={false}
              isMulti
              isDisabled={isFilterDisabled("tags")}
              isSearchable
              placeholder="Search and select tags..."
              options={generateOptions("tags", filterInfo.tags)}
              value={getSelectValue("tags")}
              onChange={(selectedOptions) =>
                handleSelectChange("tags", selectedOptions)
              }
              styles={getSelectStyles(isFilterDisabled("tags"))}
              noOptionsMessage={() => "No tags available"}
            />
          </div> */}

          {/* ICP Score - slider - dynamic - COMMENTED OUT */}
          {/* <div
            className={`mb-8 ${
              isSliderDisabled("icpScore") ? "opacity-50" : ""
            }`}
          >
            <div className="mb-6">
              <span className="text-base font-normal text-[#2a2a2a]">
                ICP Score
                {isSliderDisabled("icpScore") && (
                  <span className="text-xs text-gray-400 ml-2">
                    (No data available)
                  </span>
                )}
              </span>
            </div>
            <DualThumbSlider
              min={getSliderRange("icpScore", 0, 10).min}
              max={getSliderRange("icpScore", 0, 10).max}
              value={
                filters?.icpScore || [
                  getSliderRange("icpScore", 0, 10).min,
                  getSliderRange("icpScore", 0, 10).max,
                ]
              }
              onChange={(val) => handleRangeChange("icpScore", val)}
              disabled={isSliderDisabled("icpScore")}
            />
          </div> */}

          {/* Number of Visits - slider - dynamic - COMMENTED OUT */}
          {/* <div
            className={`mb-8 ${
              isSliderDisabled("noOfVisits") ? "opacity-50" : ""
            }`}
          >
            <div className="mb-6">
              <span className="text-base font-normal text-[#2a2a2a]">
                Number of Visits
                {isSliderDisabled("noOfVisits") && (
                  <span className="text-xs text-gray-400 ml-2">
                    (No data available)
                  </span>
                )}
              </span>
            </div>
            <DualThumbSlider
              min={getSliderRange("noOfVisits", 0, 50).min}
              max={getSliderRange("noOfVisits", 0, 50).max}
              value={
                filters?.numberOfVisits || [
                  getSliderRange("noOfVisits", 0, 50).min,
                  getSliderRange("noOfVisits", 0, 50).max,
                ]
              }
              onChange={(val) => handleRangeChange("numberOfVisits", val)}
              disabled={isSliderDisabled("noOfVisits")}
            />
          </div> */}

          {/* Exact Visitor - radio selection True(Yes) Or False(No) */}
          <div className="mb-8">
            <label className="block text-base font-normal text-[#2a2a2a] mb-3">
              Visitor Type
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <Tooltip
                  content="100% accurate identification of the exact person"
                  trigger="hover"
                  style="light"
                  placement="right"
                >
                  <input
                    type="radio"
                    name="exactVisitor"
                    value="true"
                    checked={filters.exactVisitor?.includes("true")}
                    onChange={(e) =>
                      handleRangeChange("exactVisitor", e.target.value)
                    }
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">Exact</span>
                </Tooltip>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="exactVisitor"
                  value="false"
                  checked={filters.exactVisitor?.includes("false")}
                  onChange={(e) =>
                    handleRangeChange("exactVisitor", e.target.value)
                  }
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600">Suggested</span>
              </label>
            </div>
          </div>

          {/* Has Phone Number - radio selection True(Yes) Or False(No) */}
          <div className="mb-8">
            <label className="block text-base font-normal text-[#2a2a2a] mb-3">
              Has Phone Number
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="hasPhoneNumber"
                  value="true"
                  checked={filters.hasPhoneNumber?.includes("true")}
                  onChange={(e) =>
                    handleRangeChange("hasPhoneNumber", e.target.value)
                  }
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600">Yes</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="hasPhoneNumber"
                  value="false"
                  checked={filters.hasPhoneNumber?.includes("false")}
                  onChange={(e) =>
                    handleRangeChange("hasPhoneNumber", e.target.value)
                  }
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600">No</span>
              </label>
            </div>
          </div>

          {/* 11. Revenue - slider - 1M to 100M - revenue_lower_bound, revenue_upper_bound */}
          {/* <div
            className={`mb-8 ${
              isSliderDisabled("revenue") ? "opacity-50" : ""
            }`}
          >
            <div className="mb-6">
              <span className="text-base font-normal text-[#2a2a2a]">
                Revenue{" "}
                <span className="text-xs text-gray-400 ml-2">
                  (In Millions)
                </span>
                {isSliderDisabled("revenue") && (
                  <span className="text-xs text-gray-400 ml-2">
                    (No data available)
                  </span>
                )}
              </span>
            </div>
            <DualThumbSlider
              min={getSliderRange("revenue", 1, 100).min || 1}
              max={getSliderRange("revenue", 1, 100).max || 100}
              value={
                filters?.revenue && filters.revenue.length === 2
                  ? filters.revenue
                  : [
                      getSliderRange("revenue", 1, 100).min || 1,
                      getSliderRange("revenue", 1, 100).max || 100,
                    ]
              }
              onChange={(val) => handleRangeChange("revenue", val)}
              disabled={isSliderDisabled("revenue")}
            />
          </div> */}

          {/* Employees - slider - dynamic - COMMENTED OUT */}
          {/* <div
            className={`mb-8 ${
              isSliderDisabled("employees") ? "opacity-50" : ""
            }`}
          >
            <div className="mb-6">
              <span className="text-base font-normal text-[#2a2a2a]">
                Employees
                {isSliderDisabled("employees") && (
                  <span className="text-xs text-gray-400 ml-2">
                    (No data available)
                  </span>
                )}
              </span>
            </div>
            <DualThumbSlider
              min={getSliderRange("employees", 0, 700).min}
              max={getSliderRange("employees", 0, 700).max}
              value={
                filters?.employees || [
                  getSliderRange("employees", 0, 700).min,
                  getSliderRange("employees", 0, 700).max,
                ]
              }
              onChange={(val) => handleRangeChange("employees", val)}
              disabled={isSliderDisabled("employees")}
            />
          </div> */}
        </div>
      )}
      {/* Exclusion Filters Modal */}
      {isExclusionModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  Exclusion Filters
                </h2>
                <p className="text-xs text-gray-500">
                Automatically exclude unwanted identified leads to improve data quality and reduce noise.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsExclusionModalOpen(false)}
                  className="rounded-full p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                >
                  <span className="sr-only">Close</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="space-y-6">
              {isLoadingExclusions ? (
                <>
                  {[1, 2, 3, 4].map((categoryIndex) => (
                    <div key={categoryIndex}>
                      {/* Category Header Skeleton */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="h-4 w-48 bg-gray-300 rounded skeleton-wave"></div>
                        <div className="h-4 w-12 bg-gray-300 rounded skeleton-wave"></div>
                      </div>
                      
                      {categoryIndex === 1 ? (
                        <div className="h-10 w-full bg-gray-300 rounded skeleton-wave"></div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {[1, 2, 3, 4, 5, 6].map((tagIndex) => (
                            <div
                              key={tagIndex}
                              className="h-8 w-24 bg-gray-300 rounded-lg skeleton-wave"
                            ></div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </>
              ) : (
                // Hardcoded 4 categories
                ["industries", "employeeSizes", "companyTypes", "sectors"].map(
                  (categoryKey) => {
                    const categoryOptions = exclusionOptions[categoryKey] || [];
                    const selectedValues = selectedExclusionOptions[categoryKey] || [];

                    if (categoryOptions.length === 0) {
                      return null;
                    }

                    return (
                      <div key={categoryKey}>
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-xs font-semibold tracking-wide text-gray-500">
                            {categoryDisplayNames[categoryKey]}
                          </p>
                          {selectedValues.length > 0 && (
                            <button
                              type="button"
                              onClick={() => clearExclusionCategory(categoryKey)}
                              className="text-[11px] font-medium text-blue-500 hover:text-blue-700"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                        {categoryKey === "industries" ? (
                          <Select
                            isMulti
                            isSearchable
                            hideSelectedOptions={false}
                            isClearable={false}
                            placeholder="Search and select industries to exclude..."
                            options={categoryOptions.map((label) => ({
                              value: label,
                              label,
                            }))}
                            value={selectedValues.map((value) => ({
                              value,
                              label: value,
                            }))}
                            onChange={(selected) => {
                              const values = selected
                                ? selected.map((option) => option.value)
                                : [];
                              setSelectedExclusionOptions((prev) => ({
                                ...prev,
                                [categoryKey]: values,
                              }));
                            }}
                            styles={getSelectStyles(false)}
                            className="filter-select"
                            classNamePrefix="filter-select"
                          />
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {categoryOptions.map((label) => {
                              const isSelected = selectedValues.includes(label);
                              return (
                                <button
                                  key={label}
                                  type="button"
                                  onClick={() => toggleExclusionOption(categoryKey, label)}
                                  className={`px-3 py-1.5 text-xs rounded-lg border transition-colors flex items-center gap-1.5 ${
                                    isSelected
                                      ? "bg-blue-100 border-blue-400 text-blue-700"
                                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                                  }`}
                                >
                                  {isSelected && (
                                    <svg
                                      className="w-3 h-3"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M5 13l4 4L19 7"
                                      />
                                    </svg>
                                  )}
                                  {label}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }
                )
              )}
              {/* if no data available then show message exclusionOptions objects childs arrays length is 0 */}
              {Object.values(exclusionOptions).every(array => array.length === 0) && !isLoadingExclusions && (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">No exclusion filters available</p>
                </div> 
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancelExclusions}
                disabled={isSavingExclusions}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveExclusions}
                disabled={isSavingExclusions || isLoadingExclusions}
                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSavingExclusions && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Target Countries Modal */}
      {isTargetCountriesModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-visible p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">Target Countries</h2>
                <p className="text-xs text-gray-500">
                  Select the countries you want to target. Only leads from these countries will be shown.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsTargetCountriesModalOpen(false)}
                className="rounded-full p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                <span className="sr-only">Close</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              {isLoadingTargetCountries ? (
                <div className="h-10 w-full bg-gray-300 rounded skeleton-wave"></div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold tracking-wide text-gray-500">COUNTRIES</p>
                    {selectedTargetCountries.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedTargetCountries([])}
                        className="text-[11px] font-medium text-blue-500 hover:text-blue-700"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <Select
                    isMulti
                    isSearchable
                    hideSelectedOptions={false}
                    isClearable={false}
                    placeholder="Search and select countries..."
                    options={allCountries}
                    value={selectedTargetCountries}
                    onChange={(selected) => setSelectedTargetCountries(selected || [])}
                    styles={getSelectStyles(false)}
                    className="filter-select"
                    classNamePrefix="filter-select"
                    noOptionsMessage={() => "No countries found"}
                  />
                </>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsTargetCountriesModalOpen(false)}
                disabled={isSavingTargetCountries}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveTargetCountries}
                disabled={isSavingTargetCountries || isLoadingTargetCountries}
                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSavingTargetCountries && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        setIsOpen={setIsUpgradeModalOpen}
      />
    </div>
  );
};

export default LevelFilters;
